import React, { useEffect, useState } from 'react'
import {
  Grid,
  Typography,
  Select,
  MenuItem,
  FormControl,
  Box,
  Checkbox,
  ListItemText,
  ListSubheader,
  IconButton,
  InputAdornment,
  Button,
  Drawer,
  useTheme,
  Tooltip,
} from '@mui/material'
import ClearIcon from '@mui/icons-material/Clear'
import CloseIcon from '@mui/icons-material/Close'
import { useSearchParams } from 'react-router-dom'
import { filterDefinitions } from './filterDefinitions'


export const FilterHeader = ({
  filterValues,
  handleFilterChange,
  mobile,
}) => {
  const theme = useTheme()
  const [searchParams, setSearchParams] = useSearchParams()
  const { specialties = [], candidateTypes = [] } = filterValues
  const [candidateTypeId, setCandidateTypeId] = useState('')
  const [selectedSpecialties, setSelectedSpecialties] = useState([])
  const [professionDrawerOpen, setProfessionDrawerOpen] = useState(false)
  const [specialtyDrawerOpen, setSpecialtyDrawerOpen] = useState(false)
  const [professionSelectOpen, setProfessionSelectOpen] = useState(false)
  const [specialtySelectOpen, setSpecialtySelectOpen] = useState(false)

  useEffect(() => {
    const candidateTypeIdFromParams = searchParams.get('candidateTypeId')
    if (candidateTypeIdFromParams) {
      if (candidateTypeIdFromParams !== candidateTypeId) {
        const selectedProfession = candidateTypes.find(
          option => String(option.value) === candidateTypeIdFromParams,
        )
        if (selectedProfession) {
          setCandidateTypeId(selectedProfession.value)
        } else {
          setCandidateTypeId('')
        }
      }
    } else {
      if (candidateTypeId !== '') {
        setCandidateTypeId('')
      }
    }
  }, [searchParams, candidateTypes])

  useEffect(() => {
    const searchParamSpecialties = searchParams.get('specialtyIds')?.split('|') || []
    const specialtyObjects = searchParamSpecialties.map(id => {
      const specialty = specialties.find(s => s.value === id)
      return specialty ? { name: specialty.name, value: specialty.value } : { name: '', value: id }
    })
    if (JSON.stringify(specialtyObjects) !== JSON.stringify(selectedSpecialties)) {
      setSelectedSpecialties(specialtyObjects.length > 0 ? specialtyObjects : [])
    }
  }, [searchParams, specialties])

  const handleProfessionChange = event => {
    const newSelectedProfession = event.target.value
    const filterDefinition = filterDefinitions.find(filter => filter.name === 'candidateTypes')
    handleFilterChange({
      filterDefinition,
      selectedValues: newSelectedProfession ? [{ value: newSelectedProfession }] : [],
    })

    setCandidateTypeId(newSelectedProfession)
    const newSearchParams = new URLSearchParams(searchParams)
    if (newSelectedProfession) {
      newSearchParams.set('candidateTypeId', String(newSelectedProfession))
    } else {
      newSearchParams.delete('candidateTypeId')
    }
    newSearchParams.delete('specialtyIds')
    setSearchParams(newSearchParams)
    setSelectedSpecialties([])
  }

  const handleSpecialtyChange = event => {
    const value = event.target.value

    if (value.includes('all')) {
      if (selectedSpecialties.length === specialties.length) {
        setSelectedSpecialties([])
        const filterDefinition = filterDefinitions.find(filter => filter.name === 'specialty')
        handleFilterChange({
          filterDefinition,
          selectedValues: [],
        })
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('specialtyIds')
        setSearchParams(newSearchParams)
      } else {
        const allSpecialties = specialties.map(s => ({ name: s.name, value: s.value }))
        setSelectedSpecialties(allSpecialties)
        const filterDefinition = filterDefinitions.find(filter => filter.name === 'specialty')
        handleFilterChange({
          filterDefinition,
          selectedValues: allSpecialties,
        })
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.set('specialtyIds', specialties.map(s => s.value).join('|'))
        setSearchParams(newSearchParams)
      }
    } else {
      const selectedValues = value.filter(val => val !== 'all')
      const newSelectedSpecialties = selectedValues
        .map(val => {
          const specialty = specialties.find(s => s.value === val)
          return specialty ? { name: specialty.name, value: specialty.value } : null
        })
        .filter(s => s !== null)

      setSelectedSpecialties(newSelectedSpecialties)

      const filterDefinition = filterDefinitions.find(filter => filter.name === 'specialty')
      handleFilterChange({
        filterDefinition,
        selectedValues: newSelectedSpecialties,
      })

      const newSearchParams = new URLSearchParams(searchParams)
      if (newSelectedSpecialties.length > 0) {
        newSearchParams.set('specialtyIds', newSelectedSpecialties.map(s => s.value).join('|'))
      } else {
        newSearchParams.delete('specialtyIds')
      }
      setSearchParams(newSearchParams)
    }
  }

  const clearProfession = () => {
    setCandidateTypeId('')
    handleProfessionChange({ target: { value: '' } })
  }

  const clearSpecialties = () => {
    setSelectedSpecialties([])
    handleSpecialtyChange({ target: { value: [] } })
  }

  const getProfessionDisplayName = () => {
    if (!candidateTypeId) return 'All Professions'
    const selectedOption = candidateTypes.find(option => String(option.value) === String(candidateTypeId))
    const displayName = selectedOption?.name || ''
    return displayName.length > 35 ? `${displayName.substring(0, 30)}...` : displayName
  }

  const getSpecialtyDisplayName = () => {
    if (selectedSpecialties.length === 0) return 'All Specialties'
    const selectedNames = selectedSpecialties.map(s => s.name || '').join(', ')
    return selectedNames.length > 35 ? `${selectedNames.substring(0, 30)}...` : selectedNames
  }

  return (
    <>
      <Grid
        container
        direction='row'
        xs={12}
        justifyContent={mobile ? 'center' : 'flex-start'}
        alignItems='center'
        spacing={mobile ? 2 : 0}
        sx={{
          margin: '0px',
                    backgroundColor: theme.palette.background.dark,
          padding: '10px 0px',
          borderRadius: 0,
          overflow: 'hidden',
          textAlign: 'center',
          flexWrap: mobile ? 'wrap' : 'nowrap',
        }}
      >
        {candidateTypes.length > 0 && (
        <Grid item xs={12} container alignItems='center' justifyContent='flex-start'>
          <Box
            display='flex'
            alignItems='center'
            color='white'
            flexDirection={mobile ? 'column' : 'row'}
            justifyContent='flex-start'
            width='100%'
            sx={{ padding: { xs: '4px 16px', md: '8px 0px' } }}
          >
            <Grid
              container
              item
              flexDirection='row'
              xs={11}
              justifyContent='flex-start'
              alignItems='center'
              sx={{
                textAlign: 'left',
                flexDirection: { xs: 'column', md: 'row' },
                width: '100%',
                gap: 1,
              }}
            >
              <Grid item xs={12} md="auto" sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                width: '100%',
                maxWidth: '100%',
              }}>
                {mobile ? (
                  <Button
                    variant="outlined"
                    onClick={() => setProfessionDrawerOpen(true)}
                    sx={{
                      width: '100%',
                      height: '48px',
                      justifyContent: 'space-between',
                      textTransform: 'none',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      marginBottom: '8px',
                      color: theme.palette.primary.dark,
                      borderColor: theme.palette.divider,
                      '&:hover': {
                        backgroundColor: theme.palette.grey[50],
                      },
                    }}
                    endIcon={candidateTypeId && (
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation()
                          clearProfession()
                        }}
                        sx={{ padding: 0, fontSize: '16px', color: theme.palette.primary.main }}
                      >
                        <ClearIcon fontSize='small' />
                      </IconButton>
                    )}
                  >
                    {getProfessionDisplayName()}
                  </Button>
                ) : (
                  <>
                    <Typography
                      variant='body1'
                      sx={{
                        color: 'white',
                        fontWeight: 600,
                        marginRight: 1,
                        whiteSpace: 'nowrap',
                        minWidth: '105px',
                        textAlign: 'left',
                      }}
                    >
                      Professions:
                    </Typography>
                    <FormControl
                      variant='standard'
                      sx={{
                        margin: '0',
                        minWidth: 300,
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        width: '100%',
                        maxWidth: '100%',
                        flex: 1,
                        overflow: 'hidden',
                      }}
                    >
                    <Select
                    displayEmpty
                    value={candidateTypeId || ''}
                    onChange={handleProfessionChange}
                    disableUnderline
                    variant='standard'
                    endAdornment={
                      candidateTypeId && (
                        <InputAdornment position='end' sx={{ marginRight: '32px' }}>
                          <IconButton
                            onClick={clearProfession}
                            sx={{ padding: 0, fontSize: '16px', color: theme.palette.primary.dark }}
                            aria-label="Clear profession filter"
                          >
                            <ClearIcon fontSize='small' />
                          </IconButton>
                        </InputAdornment>
                      )
                    }
                    renderValue={selected => {
                      if (!selected || selected.length === 0) {
                        return <>All Professions</>
                      }
                      const selectedOption = candidateTypes.find(option => String(option.value) === String(selected))
                      const displayName = selectedOption?.name || ''
                      const truncated = mobile && displayName.length > 35
                      return truncated ? (
                        <Tooltip title={displayName} arrow>
                          <span>{`${displayName.substring(0, 30)}...`}</span>
                        </Tooltip>
                      ) : displayName
                    }}
                    sx={{
                      color: theme.palette.primary.dark,
                      '& .MuiSelect-select': { 
                        padding: '0 10px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: mobile ? 'calc(100vw - 80px)' : 'none',
                      },
                      backgroundColor: 'transparent',
                      '& .MuiSelect-icon': { right: '8px' }, // Align arrow to right edge
                      padding: '5px',
                      width: '100%',
                      maxWidth: '100%',
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: mobile ? '60vh' : 300,
                          width: mobile ? '90vw' : 'auto',
                          maxWidth: mobile ? '90vw' : 'auto',
                        },
                      },
                      anchorOrigin: {
                        vertical: 'bottom',
                        horizontal: 'left',
                      },
                      transformOrigin: {
                        vertical: 'top',
                        horizontal: 'left',
                      },
                      ...(mobile && {
                        sx: {
                          '& .MuiPaper-root': {
                            borderRadius: '12px 12px 0 0',
                            marginTop: '8px',
                          },
                          '& .MuiMenuItem-root': {
                            minHeight: '48px',
                            fontSize: '16px',
                          },
                        },
                      }),
                    }}
                  >
                    <MenuItem value=''>All</MenuItem>
                    {candidateTypes.map(option => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.name}
                      </MenuItem>
                    ))}
                      </Select>
                    </FormControl>
                  </>
                )}
              </Grid>

              {specialties.length > 0 && (
                <Grid item xs={12} md="auto" sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  width: '100%',
                  maxWidth: '100%',
                }}>
                  {mobile ? (
                    <Button
                      variant="outlined"
                      onClick={() => setSpecialtyDrawerOpen(true)}
                      sx={{
                        width: '100%',
                        height: '48px',
                        justifyContent: 'space-between',
                        textTransform: 'none',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        marginBottom: '8px',
                        color: theme.palette.primary.dark,
                        borderColor: theme.palette.divider,
                        '&:hover': {
                          backgroundColor: theme.palette.grey[50],
                        },
                      }}
                      endIcon={selectedSpecialties.length > 0 && (
                        <IconButton
                          onClick={(e) => {
                            e.stopPropagation()
                            clearSpecialties()
                          }}
                          sx={{ padding: 0, fontSize: '16px', color: theme.palette.primary.main }}
                        >
                          <ClearIcon fontSize='small' />
                        </IconButton>
                      )}
                    >
                      {selectedSpecialties.length === 0 || selectedSpecialties.length === specialties.length
                        ? 'All Specialties'
                        : getSpecialtyDisplayName()
                      }
                    </Button>
                  ) : (
                    <>
                      <Typography
                        variant='body1'
                        sx={{
                          color: 'white',
                          fontWeight: 600,
                          marginRight: 1,
                          whiteSpace: 'nowrap',
                          minWidth: '105px',
                          textAlign: 'left',
                        }}
                      >
                        Specialties:
                      </Typography>
                      <FormControl
                        variant='standard'
                        sx={{
                          margin: '0',
                          minWidth: 300,
                          backgroundColor: 'white',
                          borderRadius: '4px',
                          width: '100%',
                          maxWidth: '100%',
                          flex: 1,
                          overflow: 'hidden',
                        }}
                      >
                      <Select
                      multiple
                      displayEmpty
                      value={
                        selectedSpecialties.length === 0
                          ? []
                          : selectedSpecialties.length === specialties.length
                            ? ['all']
                            : selectedSpecialties.map(s => s.value)
                      }
                      onChange={handleSpecialtyChange}
                      disableUnderline
                      variant='standard'
                      endAdornment={
                        selectedSpecialties.length > 0 && (
                          <InputAdornment position='end' sx={{ marginRight: '32px' }}>
                            <IconButton
                              onClick={clearSpecialties}
                              sx={{
                                padding: 0,
                                fontSize: '16px',
                                color: theme.palette.primary.dark,
                              }}
                              aria-label="Clear specialty filter"
                            >
                              <ClearIcon fontSize='small' />
                            </IconButton>
                          </InputAdornment>
                        )
                      }
                      renderValue={selected => {
                        if (selected.length === 0 || selected.includes('all')) {
                          return 'All Specialties'
                        }
                        const selectedNames = selected
                          .filter(value => value && value !== 'all')
                          .map(value => specialties.find(s => s.value === value)?.name || '')
                          .join(', ')
                        const truncated = mobile && selectedNames.length > 35
                        return truncated ? (
                          <Tooltip title={selectedNames} arrow>
                            <span>{`${selectedNames.substring(0, 30)}...`}</span>
                          </Tooltip>
                        ) : selectedNames
                      }}
                      sx={{
                        color: theme.palette.primary.dark,
                        '& .MuiSelect-select': { 
                          padding: '0 10px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: mobile ? 'calc(100vw - 80px)' : 'none',
                        },
                        backgroundColor: 'transparent',
                        '& .MuiSelect-icon': { right: '8px' }, // Align arrow to right edge
                        padding: '5px',
                        width: '100%',
                        maxWidth: '100%',
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: mobile ? '60vh' : 300,
                            width: mobile ? '90vw' : 'auto',
                            maxWidth: mobile ? '90vw' : 'auto',
                          },
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left',
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left',
                        },
                        ...(mobile && {
                          sx: {
                            '& .MuiPaper-root': {
                              borderRadius: '12px 12px 0 0',
                              marginTop: '8px',
                            },
                            '& .MuiMenuItem-root': {
                              minHeight: '48px',
                              fontSize: '16px',
                            },
                          },
                        }),
                      }}
                    >
                      <MenuItem key='all' value='all'>
                        <Checkbox
                          checked={selectedSpecialties.length === specialties.length}
                          indeterminate={
                            selectedSpecialties.length > 0 &&
                            selectedSpecialties.length < specialties.length
                          }
                        />
                        <ListItemText primary='All' />
                      </MenuItem>
                      {specialties.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          <Checkbox
                            checked={selectedSpecialties.some(s => s.value === option.value)}
                          />
                          <ListItemText primary={option.name} />
                        </MenuItem>
                      ))}
                      </Select>
                    </FormControl>
                  </>
                  )}
                </Grid>
              )}
            </Grid>
          </Box>
        </Grid>
        )}
      </Grid>

      {/* Profession Drawer for Mobile */}
      <Drawer
        anchor='bottom'
        open={professionDrawerOpen}
        onClose={() => setProfessionDrawerOpen(false)}
        PaperProps={{
          sx: {
            padding: 2,
            maxHeight: '90vh',
            overflowY: 'auto',
            zIndex: 1400,
          },
        }}
        sx={{
          zIndex: 1400,
          '& .MuiBackdrop-root': {
            backgroundColor: theme.palette.action.disabledBackground,
            cursor: 'pointer',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 2,
            paddingY: 1,
            backgroundColor: theme.palette.grey[50],
            borderRadius: '8px',
            paddingX: 2,
          }}
        >
          <Box>
            <Typography variant='h6' sx={{ color: theme.palette.text.primary }}>
              Professions
            </Typography>
            <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
              Selections apply automatically
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setProfessionDrawerOpen(false)}
            sx={{
              width: '36px',
              height: '36px',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                backgroundColor: theme.palette.grey[100],
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <FormControl fullWidth>
          <Select
            displayEmpty
            value={candidateTypeId || ''}
            open={professionSelectOpen}
            onOpen={() => setProfessionSelectOpen(true)}
            onClose={() => setProfessionSelectOpen(false)}
            onChange={(e) => {
              handleProfessionChange(e)
              setProfessionSelectOpen(false)
              setProfessionDrawerOpen(false)
            }}
            MenuProps={{
              sx: { zIndex: 1500 },
            }}
            sx={{
              '& .MuiMenuItem-root': {
                minHeight: '48px',
                fontSize: '16px',
              },
            }}
          >
            <ListSubheader
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: theme.palette.background.paper,
                py: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                Professions
              </Typography>
              <IconButton
                onClick={() => setProfessionSelectOpen(false)}
                sx={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': { backgroundColor: theme.palette.grey[100] },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </ListSubheader>
            <MenuItem value=''>All</MenuItem>
            {candidateTypes.map(option => (
              <MenuItem key={option.value} value={option.value}>
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Drawer>

      {/* Specialty Drawer for Mobile */}
      <Drawer
        anchor='bottom'
        open={specialtyDrawerOpen}
        onClose={() => setSpecialtyDrawerOpen(false)}
        PaperProps={{
          sx: {
            padding: 2,
            maxHeight: '90vh',
            overflowY: 'auto',
            zIndex: 1400,
          },
        }}
        sx={{
          zIndex: 1400,
          '& .MuiBackdrop-root': {
            backgroundColor: theme.palette.action.disabledBackground,
            cursor: 'pointer',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 2,
            paddingY: 1,
            backgroundColor: theme.palette.grey[50],
            borderRadius: '8px',
            paddingX: 2,
          }}
        >
          <Box>
            <Typography variant='h6' sx={{ color: theme.palette.text.primary }}>
              Specialties
            </Typography>
            <Typography variant='caption' sx={{ color: theme.palette.text.secondary }}>
              Selections apply automatically
            </Typography>
          </Box>
          <IconButton 
            onClick={() => setSpecialtyDrawerOpen(false)}
            sx={{
              width: '36px',
              height: '36px',
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              '&:hover': {
                backgroundColor: theme.palette.grey[100],
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <FormControl fullWidth>
          <Select
            multiple
            displayEmpty
            open={specialtySelectOpen}
            onOpen={() => setSpecialtySelectOpen(true)}
            onClose={() => setSpecialtySelectOpen(false)}
            value={
              selectedSpecialties.length === 0
                ? []
                : selectedSpecialties.length === specialties.length
                  ? ['all']
                  : selectedSpecialties.map(s => s.value)
            }
            onChange={(e) => {
              handleSpecialtyChange(e)
            }}
            renderValue={selected => {
              if (selected.length === 0 || selected.includes('all')) {
                return 'All Specialties'
              }
              const selectedNames = selected
                .filter(value => value && value !== 'all')
                .map(value => specialties.find(s => s.value === value)?.name || '')
                .join(', ')
              return selectedNames.length > 50
                ? `${selectedNames.substring(0, 45)}...`
                : selectedNames
            }}
            MenuProps={{
              sx: { zIndex: 1500 },
            }}
            sx={{
              '& .MuiMenuItem-root': {
                minHeight: '48px',
                fontSize: '16px',
              },
            }}
          >
            <ListSubheader
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: theme.palette.background.paper,
                py: 1,
                borderBottom: `1px solid ${theme.palette.divider}`,
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                Specialties
              </Typography>
              <IconButton
                onClick={() => setSpecialtySelectOpen(false)}
                sx={{
                  width: '32px',
                  height: '32px',
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                  '&:hover': { backgroundColor: theme.palette.grey[100] },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </ListSubheader>
            <MenuItem key='all' value='all'>
              <Checkbox
                checked={selectedSpecialties.length === specialties.length}
                indeterminate={
                  selectedSpecialties.length > 0 &&
                  selectedSpecialties.length < specialties.length
                }
              />
              <ListItemText primary='All' />
            </MenuItem>
            {specialties.map(option => (
              <MenuItem key={option.value} value={option.value}>
                <Checkbox
                  checked={selectedSpecialties.some(s => s.value === option.value)}
                />
                <ListItemText primary={option.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Drawer>
    </>
  )
}

export default FilterHeader
